import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class GetisOrd extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.getisord',
        group: 'Statistics',
        title: 'Getis Ord',
        description: `
                    This function computes the Getis-Ord Gi* statistic for each index in the input table.
                `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'indexcol',
          type: WorkflowComponentParamType.Column,
          title: 'Index column',
          parent: 'source',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        },
        {
          name: 'valuecol',
          type: WorkflowComponentParamType.Column,
          title: 'Value column',
          parent: 'source',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'kernel',
          type: WorkflowComponentParamType.Selection,
          title: 'Kernel function for spatial weights',
          options: ['uniform', 'triangular', 'quadratic', 'quartic', 'gaussian'],
          providers: [Provider.BigQuery, Provider.Snowflake]
        },
        {
          name: 'databricks_kernel',
          type: WorkflowComponentParamType.Selection,
          title: 'Kernel function for spatial weights',
          options: [
            'uniform',
            'triangular',
            'quadratic',
            'quartic',
            'gaussian',
            'bounded_gaussian',
            'inverse',
            'inverse_square',
            'exponential'
          ],
          providers: [Provider.DatabricksRest]
        },
        {
          name: 'size',
          type: WorkflowComponentParamType.Number,
          title: 'Size',
          default: 3,
          min: 1,
          max: 10
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)

    const source = preparedParams.source as string
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)
    let query = ''
    switch (connection.type) {
      case Provider.BigQuery: {
        let outTable = tablename
        let copyFromTempTable = ''
        if (codeType === WorkflowCodeType.StoredProcedure) {
          outTable = `${connection.tempLocation}.${tempTableName(componentName, preparedParams.source)}`
          copyFromTempTable = connection.getCreateTempTableQuery(
            tablename,
            `SELECT * FROM ${connection.quoteName(outTable)}`,
            codeType,
            null
          )
        }
        query = dedent`
              DECLARE grid_type STRING;
              DECLARE grid_resolution INT64;

              CALL ${at}.__CHECK_GRID_INDEX_COLUMN(
                'SELECT * FROM ${connection.quoteName(source)}',
                '${params.indexcol as string}',
                grid_type,
                grid_resolution
                );

              ${
                codeType === WorkflowCodeType.StoredProcedure
                  ? `DROP TABLE IF EXISTS ${connection.quoteName(outTable)};`
                  : ''
              }
              IF grid_type = 'quadbin' THEN
                  CALL ${at}.GETIS_ORD_QUADBIN_TABLE(
                      'SELECT * FROM ${connection.quoteName(source)}',
                      '${outTable}',
                      '${params.indexcol as string}',
                      '${params.valuecol as string}',
                      CAST(${params.size} AS INTEGER),
                      '${params.kernel}'
                  );
              ELSEIF grid_type = 'h3' THEN
                  CALL ${at}.GETIS_ORD_H3_TABLE(
                      'SELECT * FROM ${connection.quoteName(source)}',
                      '${outTable}',
                      '${params.indexcol as string}',
                      '${params.valuecol as string}',
                      CAST(${params.size} AS INTEGER),
                      '${params.kernel}'
                  );
              ELSE
                  RAISE USING MESSAGE = 'Invalid spatial index column, please select a column that contains h3 or quadbin indexes.';
              END IF;
              ${copyFromTempTable}
          `
        break
      }
      case Provider.Snowflake:
        query = dedent`
              DECLARE
                grid_type STRING;
                WRONG_GRID_TYPE_EXCEPTION EXCEPTION;
              BEGIN
                grid_type := (
                  CALL ${at}._CHECK_GRID_INDEX_COLUMN(
                    'SELECT * FROM ${source}',
                    '${params.indexcol as string}'
                  )
                );
                IF (grid_type != 'unsupported') THEN
                  ${
                    codeType === WorkflowCodeType.StoredProcedure
                      ? `DROP TABLE IF EXISTS ${connection.quoteName(tablename)};`
                      : ''
                  }
                  EXECUTE IMMEDIATE '
                      CALL ${at}.GETIS_ORD_' || grid_type || '(
                          ''SELECT * FROM ${connection.quoteName(source)}'',
                          ''${tablename}'',
                          ''${params.indexcol as string}'',
                          ''${params.valuecol as string}'',
                          CAST(${params.size} AS INTEGER),
                          ''${params.kernel}''
                      );
                  ';
                ELSE
                  RAISE WRONG_GRID_TYPE_EXCEPTION;
                END IF;
              END;
            `
        break
      case Provider.DatabricksRest:
        query = dedent`
            from carto_statistics.GETIS_ORD import main as getis_ord

            getis_ord(
                f'SELECT ${params.indexcol as string}, ${params.valuecol as string} FROM ${connection.quoteName(
          source
        )}',
                '${params.indexcol as string}',
                '${params.valuecol as string}',
                '${connection.quoteName(tablename)}',
                kring_size=int(${params.size}),
                kernel_function='${params.databricks_kernel}'
            )
          `
        break
    }

    query = connection.wrapCodeInIfTableExistsBlock(query, [tablename], codeType)

    if (connection.type === Provider.DatabricksRest) {
      query = dedent`
      ${query}
      ${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(connection.optimizationStatsQuery(tablename))}
    `
    }
    return {
      outputs: { result: tablename },
      query
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    const indexType = columns.source.find((c) => c.name === params.indexcol)?.type
    return [
      {
        name: connection.type === Provider.DatabricksRest ? (params.indexcol as string) : 'index',
        type: indexType as ColumnDataType
      },
      {
        name: 'gi',
        type: ColumnDataType.Number
      },
      {
        name: 'p_value',
        type: ColumnDataType.Number
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { GetisOrd }
